/**
 * Surveillance cameras.
 *
 * Replaces the traffic-signal layer, which carried locations but no phase
 * data and therefore said nothing about the city. Cameras say a great deal,
 * and OpenStreetMap has 875 of them mapped downtown by people who went and
 * looked.
 *
 * User-contributed cameras render alongside the OSM ones, marked as such —
 * "somebody saw this" and "OSM has this" are different claims and the map
 * should not blur them.
 */

import { ScatterplotLayer } from "@deck.gl/layers";

import { Layer } from "./base.js";
import { palette, themeName } from "../theme/palette.js";
import { DEPTH_TESTED, OVER_EVERYTHING } from "../map/depth.js";

// A desaturated grey-purple. Cameras are context, not a headline: there are
// 875 of them and a saturated colour would turn the map into a rash.

/** Denver PD's own network. Slightly cooler, so the two sets are separable. */

// User-added ones stay a touch brighter, because a person chose to put it there.

/**
 * Plate readers, in red.
 *
 * An ALPR is not the same object as a camera watching a doorway: it reads
 * every registration that passes and keeps the record. Red is this map's
 * colour for policing, which is what these are — 915 of the 1,150 in the
 * Denver metro are Flock Safety units.
 */

const isAlpr = (c) => c.kind === "alpr";

/**
 * How high off the ground a camera sits, in metres.
 *
 * Drawn at zero they z-fight with the ground plane and shred as the camera
 * moves — and they are wrong besides. A street surveillance camera is on a
 * pole or a building corner well above head height; a plate reader is lower,
 * on its own short mast beside the carriageway; a traffic camera is highest,
 * on a mast arm over the junction.
 */
const MOUNT_HEIGHT = {
  camera: 4.5,
  alpr: 3.2,
  gunshot: 6,
  feed: 7,
  user: 4.5,
};

/** Zoom at which the camera dots stop being map marks and become objects. */
export const CAMERA_BALL_ZOOM = 17.4;

const heightOf = (c) => MOUNT_HEIGHT[c.source === "feed" ? "feed" : c.kind] ?? 4.5;

export class CameraLayer extends Layer {
  static key = "cameras";
  static label = "Cameras";
  static description = "Surveillance cameras from OSM, plus any you add";

  // The cameras themselves do not move — OSM, HALO and the plate readers change
  // on the server's 7-day cadence — but the live PICTURES age: CDOT overwrites
  // each snapshot every few minutes, and each feed carries the timestamp the
  // tooltip renders as "picture N min old". Fetched once at page load and never
  // again, that timestamp drifts until a live camera reads "16 h old — may be
  // down". So this layer polls, matched to the server's own 5-minute feed
  // cache — but a poll re-reads ONLY the feeds (~140 KB), never the far heavier
  // /api/cameras. See load(), which pulls the cameras once and the feeds every
  // time.
  static refreshMs = 5 * 60 * 1000;

  async load() {
    // Only the first load may blank the layer with "loading"; a poll keeps the
    // cameras on screen and swaps the fresh feeds in underneath, so the map
    // does not flicker every five minutes.
    if (!this._loaded) this.status = "loading";
    try {
      // Feeds always; the heavy camera sets only until they are in hand. Both
      // are kicked off together so the first load stays a single round trip.
      const feedsPromise = this._fetchFeeds();
      // Cameras are baked citywide at build time (scripts/bake.mjs) and read
      // as a static file, not the downtown-only /api/cameras. Contributions
      // stay live — someone can add a camera without a rebuild.
      const camerasPromise = this._loaded ? null : Promise.all([
        fetch("./data/cameras.json").then((r) => r.json()),
        fetch(`${this.basePath}/contributions`).then((r) => r.json()).catch(() => []),
      ]);

      const feeds = await feedsPromise;
      // Both feed sources failing on a poll must not wipe the pictures we hold.
      if (feeds) this._feeds = feeds;

      if (camerasPromise) {
        const [baked, contributions] = await camerasPromise;
        const all = baked.cameras ?? [];
        // Split by network. Each is drawn and described as its own thing —
        // Denver PD's plate readers are not the commercial Flock ones, and the
        // map should not say they are.
        this._osm = all.filter((c) => c.source === "osm");
        this._halo = all.filter((c) => c.source === "halo");
        this._pdAlpr = all.filter((c) => c.source === "pd_alpr");
        this._user = (Array.isArray(contributions) ? contributions : [])
          .filter((c) => c.kind === "camera");
        this._loaded = true;
      }

      this.status = "ready";
      this.detail = `${this._osm.length} OSM · ${this._halo.length} HALO`
        + `${this._pdAlpr.length ? ` · ${this._pdAlpr.length} PD ALPR` : ""}`;
      return null;
    } catch (error) {
      // A failed poll keeps the layer exactly as it was; only a failed first
      // load, before any cameras are in hand, marks the layer unavailable.
      if (!this._loaded) {
        this.status = "unavailable";
        this.detail = `Unavailable (${error.message})`;
      }
      return null;
    }
  }

  /**
   * The two picture registries, merged. Both public and both live:
   *   /api/feeds — CDOT's own registry, ~400 in the Denver bounds
   *   the static file — Denver city intersections, geocoded at build
   * Merged rather than split into two toggles: from the map's point of view
   * they are all "a camera you can look through". Returns null only if BOTH
   * sources fail, so a caller mid-poll can keep the pictures it already has
   * rather than blanking them.
   */
  async _fetchFeeds() {
    const [cdot, cityFeeds] = await Promise.all([
      fetch(`${this.basePath}/feeds`).then((r) => r.json()).catch(() => null),
      fetch("./data/camera-feeds.json").then((r) => r.json()).catch(() => null),
    ]);
    if (!cdot && !cityFeeds) return null;
    return [
      ...((cdot?.feeds) ?? []).map((f) => ({ ...f, owner: f.owner || "CDOT" })),
      ...((cityFeeds?.feeds) ?? []).map((f) => ({ ...f, owner: "City of Denver" })),
    ];
  }

  get loadedCount() {
    return (this._osm?.length ?? 0) + (this._halo?.length ?? 0)
      + (this._pdAlpr?.length ?? 0) + (this._user?.length ?? 0);
  }

  /** Every camera, across networks and user-added, for the viewshed calculation. */
  allCameras() {
    return [...(this._osm ?? []), ...(this._halo ?? []),
      ...(this._pdAlpr ?? []), ...(this._user ?? [])];
  }

  get feeds() {
    return this._feeds ?? [];
  }

  /** Called after a user adds one, so the map updates without a reload. */
  addUserCamera(record) {
    this._user = [...(this._user ?? []), record];
  }

  legend() {
    return [
      { key: "osm", rgb: palette().cameraOsm, label: "Camera", note: "Surveillance cameras mapped in OSM." },
      { key: "halo", rgb: palette().cameraHalo, label: "HALO camera", note: "Denver Police Department's own network." },
      { key: "alpr", rgb: palette().alpr, label: "Plate reader", note: "ALPR — reads and logs every registration that passes. Mostly Flock Safety." },
      { key: "pd_alpr", rgb: palette().alprPolice, label: "Police plate reader", note: "Denver PD's own fixed ALPR network." },
      { key: "gunshot", rgb: palette().gunshot, label: "Gunshot detector", note: "Acoustic sensor that locates gunfire." },
      { key: "feed", rgb: palette().feed, label: "Live feed", note: "City traffic camera — click to watch." },
    ];
  }

  describe(row) {
    if (row.source === "feed") {
      const rows = [["Type", "Traffic camera"]];
      if (row.route) rows.push(["Route", row.route]);
      if (row.near) rows.push(["Location", String(row.near).replace(/^in /, "")]);
      // Age, not a timestamp. "14 min ago" answers the question a timestamp
      // makes you do arithmetic for — is this picture worth believing?
      const stamped = row.views.find((v) => v.updated || v.modified);
      const when = stamped?.updated ?? stamped?.modified;
      if (when) {
        const mins = Math.max(0, Math.round((Date.now() - when) / 60000));
        rows.push(["Picture", mins < 60 ? `${mins} min old`
          : mins < 1440 ? `${Math.round(mins / 60)} h old`
          : `${Math.round(mins / 1440)} days old — camera may be down`]);
      }
      rows.push(["Owner", row.owner || "CDOT"]);
      // The feed is clickable — say so, since a hover picture gives no hint that
      // there is a live stream a click away. On touch the tip itself is the
      // button (ui/tooltip.js), so the words must point at the tip, not at a
      // click that phones cannot make.
      const touch = typeof window !== "undefined"
        && window.matchMedia?.("(pointer: coarse)").matches;
      rows.push(["", row.views?.some((v) => v.stream)
        ? (touch ? "tap here to watch live" : "Click to watch live")
        : (touch ? "tap here to expand" : "Click to expand")]);
      return {
        title: row.name || "Traffic camera",
        // The tooltip renders these as pictures; see ui/tooltip.js.
        images: row.views.slice(0, 4).map((v) => ({
          url: v.url,
          caption: (v.direction || "").replace(/^.*?(?:at|:)\s*/, "").slice(0, 28),
        })),
        rows,
      };
    }
    if (row.source === "halo") {
      const rows = [["Network", "Denver PD HALO"]];
      if (row.district) rows.push(["Police district", String(row.district)]);
      if (row.elevation) rows.push(["Mounted at", `${row.elevation} ft`]);
      // The camera's own rated reach, day and night — the layer carries it, so
      // the tooltip can say how far this one is meant to see.
      const reach = [row.dist_day && `${row.dist_day} ft day`,
        row.dist_night && `${row.dist_night} ft night`].filter(Boolean).join(", ");
      if (reach) rows.push(["Sees", reach]);
      rows.push(["Operator", row.operator || "Denver Police Department"]);
      rows.push(["Source", "Denver Open Data"]);
      return { title: row.label || "HALO camera", rows };
    }
    if (row.source === "pd_alpr") {
      return {
        title: row.label || "Police plate reader",
        rows: [
          ["Type", "Automatic plate reader"],
          ["Reads", "Every registration that passes, and logs it"],
          ["Network", "Denver PD ALPR"],
          ["Operator", row.operator || "Denver Police Department"],
          ["Source", "Denver Open Data"],
        ],
      };
    }
    if (row.source === "user") {
      return {
        title: row.label || "Camera (added)",
        rows: [
          ["Added by", row.author || "—"],
          ["Detail", row.detail || "—"],
          ["Source", "Added on this map"],
        ],
      };
    }
    const rows = [];
    if (isAlpr(row)) {
      rows.push(["Type", "Automatic plate reader"]);
      rows.push(["Reads", "Every registration that passes, and logs it"]);
    } else if (row.kind === "gunshot") {
      rows.push(["Type", "Gunshot detector"]);
    }
    if (row.brand) rows.push(["Made by", row.brand]);
    if (row.zone) rows.push(["Covers", String(row.zone).replace(/_/g, " ")]);
    if (row.kind && row.kind !== row.zone) rows.push(["Type", String(row.kind).replace(/_/g, " ")]);
    if (row.mount) rows.push(["Mount", row.mount]);
    if (row.operator) rows.push(["Operator", row.operator]);
    rows.push(["Source", "OpenStreetMap"]);
    return { title: "Surveillance camera", rows };
  }

  render(now, zoom = 0) {
    if (!this.enabled || this.status !== "ready") {
      this.visibleCount = 0;
      return [];
    }

    const all = (this._osm ?? []).map((c) => ({ ...c, source: "osm" }));
    // Plate readers and gunshot detectors draw separately and on top: they are
    // the policing infrastructure worth picking out of the field of dots, and
    // each carries its own colour. Plain cameras stay the quiet grey-purple.
    //
    // Each group is also exactly one legend row, so hiding a row is hiding a
    // group. The two that share a sublayer — plate readers and gunshot
    // detectors — are filtered by kind rather than by sublayer, or switching
    // one off would take the other with it.
    const hide = (kind) => this.isHidden(kind);
    const osm = hide("osm") ? [] : all.filter((c) => c.kind === "camera");
    const alpr = all.filter((c) =>
      (c.kind === "alpr" && !hide("alpr")) || (c.kind === "gunshot" && !hide("gunshot")));
    const halo = hide("halo") ? [] : (this._halo ?? []);
    // Denver PD's own plate readers, their own network and their own colour.
    const pdAlpr = hide("pd_alpr") ? []
      : (this._pdAlpr ?? []).map((c) => ({ ...c, source: "pd_alpr" }));
    const feeds = hide("feed") ? []
      : (this._feeds ?? []).map((f) => ({ ...f, source: "feed" }));
    // User-added cameras have no legend row of their own, so nothing can hide
    // them. Someone's own contribution disappearing behind a filter it is not
    // named in would be the worst version of this feature.
    const user = (this._user ?? []).map((c) => ({
      ...c, lng: c.lng, lat: c.lat, source: "user",
    }));
    // Counted from what is actually drawn, so the toggle's number follows the
    // legend filter down. This used to add `all`, which double-counted the
    // plate readers and gunshot detectors already inside it.
    this.visibleCount = osm.length + alpr.length + halo.length + pdAlpr.length
      + user.length + feeds.length;

    // Close in, the dots become balls.
    //
    // A ScatterplotLayer draws a disc lying flat in the world. That reads
    // correctly looking down and vanishes edge-on — at eye level with a camera
    // on a pole the dot marking it thinned to nothing, which is exactly where
    // you most want to see it. Billboarding turns the disc to face the viewer,
    // so from street level it reads as a ball on its mount.
    //
    // Only close in: from above, a field of billboards all facing the camera is
    // a field of identical circles that no longer sit on the map.
    const asBalls = zoom >= CAMERA_BALL_ZOOM;

    const common = {
      // Three dimensions: cameras are mounted, not painted on the road.
      getPosition: (d) => [d.lng, d.lat, heightOf(d)],
      billboard: asBalls,
      radiusUnits: "meters",
      stroked: true,
      filled: true,
      lineWidthUnits: "pixels",
      pickable: true,
      // A ball is a real object on a mount, so a building in front of one
      // should hide it. Flat, these are annotation and must not be swallowed
      // by the extrusion they are drawn against.
      parameters: asBalls ? DEPTH_TESTED : OVER_EVERYTHING,
      updateTriggers: { getPosition: asBalls },
    };

    return [
      new ScatterplotLayer({
        ...common,
        id: `${this.key}-osm`,
        data: osm,
        getRadius: 14,
        getFillColor: [...palette().cameraOsm, 105],
        getLineColor: [...palette().cameraOsm, 165],
        getLineWidth: 1,
        radiusMinPixels: 3,
        radiusMaxPixels: 9,
      }),
      new ScatterplotLayer({
        ...common,
        id: `${this.key}-halo`,
        data: halo,
        getRadius: 16,
        getFillColor: [...palette().cameraHalo, 125],
        getLineColor: [...palette().cameraHalo, 190],
        getLineWidth: 1.2,
        radiusMinPixels: 2.5,
        radiusMaxPixels: 9,
      }),
      new ScatterplotLayer({
        ...common,
        id: `${this.key}-alpr`,
        data: alpr,
        // The one camera sublayer whose colours are computed per feature, so
        // the accessors only re-run when a trigger says the theme moved.
        updateTriggers: {
          getPosition: asBalls,
          getFillColor: themeName(),
          getLineColor: themeName(),
        },
        getRadius: 20,
        getFillColor: (d) => [
          ...(d.kind === "gunshot" ? palette().gunshot : palette().alpr), 150,
        ],
        getLineColor: (d) => [
          ...(d.kind === "gunshot" ? palette().gunshot : palette().alpr), 235,
        ],
        getLineWidth: 1.5,
        radiusMinPixels: 4,
        radiusMaxPixels: 12,
      }),
      new ScatterplotLayer({
        ...common,
        id: `${this.key}-pdalpr`,
        data: pdAlpr,
        getRadius: 20,
        getFillColor: [...palette().alprPolice, 165],
        getLineColor: [...palette().alprPolice, 240],
        getLineWidth: 1.6,
        radiusMinPixels: 4,
        radiusMaxPixels: 12,
      }),
      // Feeds are drawn last and largest: they are the only cameras you can
      // actually look through.
      new ScatterplotLayer({
        ...common,
        id: `${this.key}-feeds`,
        data: feeds,
        getRadius: 20,
        getFillColor: [...palette().feed, 190],
        getLineColor: [...palette().feed, 240],
        getLineWidth: 2,
        radiusMinPixels: 4,
        radiusMaxPixels: 12,
      }),
      new ScatterplotLayer({
        ...common,
        id: `${this.key}-user`,
        data: user,
        getRadius: 20,
        getFillColor: [...palette().cameraUser, 150],
        getLineColor: [...palette().cameraUser, 215],
        getLineWidth: 1.5,
        radiusMinPixels: 3,
        radiusMaxPixels: 11,
      }),
    ];
  }
}
